/**
 * Analytics Utility for Education Data Collective
 * 
 * Provides centralized event tracking for Google Analytics 4
 * Tracks key user interactions, feature usage, and business metrics
 */

/**
 * Track a custom event in Google Analytics
 * @param {string} eventName - Name of the event (use snake_case)
 * @param {Object} params - Additional parameters for the event
 */
export const trackEvent = (eventName, params = {}) => {
  if (typeof window !== 'undefined' && window.gtag) {
    try {
      window.gtag('event', eventName, {
        ...params,
        timestamp: new Date().toISOString(),
        app_version: process.env.REACT_APP_VERSION || 'unknown'
      });
    } catch (error) {
      console.error('Analytics tracking error:', error);
    }
  }
};

/**
 * Track page view (automatically called by React Router, but can be manual)
 * @param {string} pagePath - Path of the page
 * @param {string} pageTitle - Title of the page
 */
export const trackPageView = (pagePath, pageTitle) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('config', window.GA_MEASUREMENT_ID || 'G-8RWK33VE4R', {
      page_path: pagePath,
      page_title: pageTitle
    });
  }
};

/**
 * Set user properties for segmentation
 * @param {Object} properties - User properties
 */
export const setUserProperties = (properties) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('set', 'user_properties', properties);
  }
};

// ============================================
// SCHOOL & SEARCH EVENTS
// ============================================

export const trackSchoolSearch = (searchParams) => {
  trackEvent('school_search', {
    search_type: searchParams.type || 'general',
    has_filters: searchParams.hasFilters || false,
    result_count: searchParams.resultCount || 0
  });
};

export const trackSchoolView = (schoolData) => {
  trackEvent('school_view', {
    school_uuid: schoolData.uuid,
    school_name: schoolData.name,
    school_type: schoolData.type || 'unknown',
    has_nces_id: !!schoolData.nces_id
  });
};

export const trackSchoolComparison = (schoolCount) => {
  trackEvent('school_comparison', {
    schools_compared: schoolCount
  });
};

// ============================================
// PROJECTION EVENTS
// ============================================

export const trackProjectionRun = (projectionData) => {
  trackEvent('projection_run', {
    school_uuid: projectionData.schoolUuid,
    projection_years: projectionData.years || 5,
    method: projectionData.method || 'default',
    has_custom_data: projectionData.hasCustomData || false
  });
};

export const trackProjectionExport = (format) => {
  trackEvent('projection_export', {
    export_format: format // 'pdf', 'excel', 'csv'
  });
};

// ============================================
// REPORT & EXPORT EVENTS
// ============================================

export const trackReportGeneration = (reportType) => {
  trackEvent('report_generation', {
    report_type: reportType // 'enrollment', 'demographic', 'competitive'
  });
};

export const trackDataExport = (exportData) => {
  trackEvent('data_export', {
    export_type: exportData.type,
    format: exportData.format,
    row_count: exportData.rowCount || 0
  });
};

// ============================================
// DASHBOARD & VISUALIZATION EVENTS
// ============================================

export const trackDashboardView = (dashboardName) => {
  trackEvent('dashboard_view', {
    dashboard_name: dashboardName
  });
};

export const trackFilterApplication = (filterData) => {
  trackEvent('filter_applied', {
    filter_type: filterData.type,
    filter_count: filterData.count || 1,
    context: filterData.context || 'unknown'
  });
};

export const trackMapInteraction = (interactionType) => {
  trackEvent('map_interaction', {
    interaction_type: interactionType // 'zoom', 'pan', 'marker_click', 'polygon_draw'
  });
};

export const trackChartInteraction = (chartType) => {
  trackEvent('chart_interaction', {
    chart_type: chartType
  });
};

// ============================================
// GEOGRAPHIC EXPLORER EVENTS
// ============================================

export const trackNearbySchoolAnalysis = (analysisData) => {
  trackEvent('nearby_school_analysis', {
    school_uuid: analysisData.schoolUuid,
    radius_miles: analysisData.radiusMiles || 5,
    schools_found: analysisData.schoolsFound || 0
  });
};

export const trackMarketShareAnalysis = (analysisData) => {
  trackEvent('market_share_analysis', {
    school_uuid: analysisData.schoolUuid,
    polygon_type: analysisData.polygonType || 'custom'
  });
};

// ============================================
// TEAM & COLLABORATION EVENTS
// ============================================

export const trackTeamAction = (actionType, teamData) => {
  trackEvent('team_action', {
    action_type: actionType, // 'create', 'join', 'leave', 'invite'
    team_id: teamData.teamId,
    member_count: teamData.memberCount || 0
  });
};

export const trackSchoolAddedToTeam = (schoolUuid, teamId) => {
  trackEvent('school_added_to_team', {
    school_uuid: schoolUuid,
    team_id: teamId
  });
};

// ============================================
// USER ENGAGEMENT EVENTS
// ============================================

export const trackFeatureUsage = (featureName) => {
  trackEvent('feature_used', {
    feature_name: featureName
  });
};

// ============================================
// PRODUCT AREA USAGE (first-party)
// ============================================

/**
 * The product areas reported on monthly. Keys must match FEATURE_AREAS in
 * backend/app/services/product_usage.py.
 */
export const PRODUCT_AREAS = {
  SCHOOL_EXPLORER: 'school_explorer',
  GEOGRAPHIC_EXPLORER: 'geographic_explorer',
  COMPETITIVE_EXPLORER: 'competitive_explorer',
  PROJECTIONS_EXPLORER: 'projections_explorer',
  AI_SUMMARY_INSIGHTS: 'ai_summary_insights',
  STUDENT_CATCHMENT_EXPLORER: 'student_catchment_explorer',
};

/**
 * Record use of a product area against the signed-in user.
 *
 * Unlike the GA events above this writes to our own database, so usage can be
 * segmented by user, network, and funder for partnership reporting. Fire and
 * forget: a tracking failure must never surface to the user.
 *
 * @param {string} feature - One of PRODUCT_AREAS
 * @param {Object} options
 * @param {'view'|'action'} options.eventType - 'view' when the tool is opened,
 *   'action' when the user runs a meaningful analysis
 * @param {string} options.action - Short name of the action, e.g. 'projection_run'
 * @param {string} options.schoolUuid - School the interaction was about, if any
 */
export const trackProductArea = (feature, { eventType = 'view', action, schoolUuid } = {}) => {
  if (!Object.values(PRODUCT_AREAS).includes(feature)) {
    console.warn(`trackProductArea: unknown feature "${feature}"`);
    return;
  }

  trackEvent('product_area_used', {
    feature_key: feature,
    event_type: eventType,
    action: action || null,
  });

  // Imported lazily so analytics stays usable in contexts without an API client
  import('./apiClient')
    .then(({ default: apiClient }) => apiClient.post('/v2/usage/track', {
      feature,
      eventType,
      action,
      schoolUuid,
    }))
    .catch(() => { /* telemetry is best-effort */ });
};

export const trackTutorialStep = (tutorialName, step) => {
  trackEvent('tutorial_step', {
    tutorial_name: tutorialName,
    step_number: step
  });
};

export const trackErrorEncountered = (errorData) => {
  trackEvent('error_encountered', {
    error_type: errorData.type || 'unknown',
    error_message: errorData.message?.substring(0, 100), // Limit message length
    context: errorData.context || 'unknown',
    severity: errorData.severity || 'error'
  });
};

// ============================================
// WORKFLOW EVENTS
// ============================================

export const trackWorkflowStart = (workflowName) => {
  trackEvent('workflow_started', {
    workflow_name: workflowName
  });
};

export const trackWorkflowComplete = (workflowName, duration) => {
  trackEvent('workflow_completed', {
    workflow_name: workflowName,
    duration_seconds: duration
  });
};

// ============================================
// AI FEATURES EVENTS
// ============================================

export const trackAIAnalysisRequest = (analysisType) => {
  trackEvent('ai_analysis_requested', {
    analysis_type: analysisType // 'competitive', 'geographic', 'demographic'
  });
};

export const trackAIAnalysisComplete = (analysisType, success) => {
  trackEvent('ai_analysis_completed', {
    analysis_type: analysisType,
    success: success
  });
};

// ============================================
// CONVERSION EVENTS (Key Business Metrics)
// ============================================

export const trackConversion = (conversionType, value) => {
  trackEvent('conversion', {
    conversion_type: conversionType,
    value: value
  });
};

// Specific high-value conversions
export const trackFirstProjection = () => {
  trackEvent('first_projection_created', {
    event_category: 'conversion',
    event_label: 'first_time_user'
  });
};

export const trackFirstReport = () => {
  trackEvent('first_report_generated', {
    event_category: 'conversion',
    event_label: 'first_time_user'
  });
};

// ============================================
// SESSION EVENTS
// ============================================

export const trackSessionStart = (userRole) => {
  trackEvent('session_start', {
    user_role: userRole
  });
};

export const trackSessionEnd = (duration) => {
  trackEvent('session_end', {
    session_duration: duration
  });
};

// ============================================
// UTILITY: Initialize Analytics with User Context
// ============================================

/**
 * Initialize analytics with user context
 * Call this after user authentication
 */
export const initializeAnalytics = (user) => {
  if (!user) return;
  
  try {
    // Set user properties for segmentation
    setUserProperties({
      user_role: user.role || 'public',
      user_id: user.uid || 'anonymous',
      has_team: !!user.teamId,
      funder: user.funder || 'none',
      account_age_days: user.createdAt 
        ? Math.floor((Date.now() - new Date(user.createdAt).getTime()) / (1000 * 60 * 60 * 24))
        : 0
    });
    
    // Track session start
    trackSessionStart(user.role || 'public');
  } catch (error) {
    console.error('Error initializing analytics:', error);
  }
};

const analyticsExports = {
  trackEvent,
  trackPageView,
  setUserProperties,
  trackSchoolSearch,
  trackSchoolView,
  trackSchoolComparison,
  trackProjectionRun,
  trackProjectionExport,
  trackReportGeneration,
  trackDataExport,
  trackDashboardView,
  trackFilterApplication,
  trackMapInteraction,
  trackChartInteraction,
  trackNearbySchoolAnalysis,
  trackMarketShareAnalysis,
  trackTeamAction,
  trackSchoolAddedToTeam,
  trackFeatureUsage,
  trackTutorialStep,
  trackErrorEncountered,
  trackWorkflowStart,
  trackWorkflowComplete,
  trackAIAnalysisRequest,
  trackAIAnalysisComplete,
  trackConversion,
  trackFirstProjection,
  trackFirstReport,
  trackSessionStart,
  trackSessionEnd,
  initializeAnalytics
};

export default analyticsExports;

